import {
  Outlet,
  useLoaderData,
  useLocation,
  useNavigation,
  useRouteError,
  type ShouldRevalidateFunctionArgs,
} from 'react-router';
import { useEffect, useRef } from 'react';
import type { Route } from './+types/layout';

import { SignUp } from '../components/auth/signup';
import { AuthContainer } from '~/components/auth/auth-container';
import { Login } from '~/components/auth/login';

import { analyticsInit, rudderAnalytics } from '~/third-party/helpers';
import { affineCookieMiddleware } from '~/middleware/affine-cookie';
import { contextInitMiddleware } from '~/middleware/context-init';
import { env } from 'cloudflare:workers';
import { cn } from '~/lib/utils';
import { getUser, accessTokenContext } from '~/api/client';
import { $path } from 'safe-routes';
import { AUTOSAVE_INTENT } from './creator/lib/use-autosave';
import { getRolesFromToken, getSession, permissionsContext, sessionStorage } from '~/lib/session';
import { getActiveRole } from '~/lib/active-role';
import {
  activeRoleCookieContext,
  activeRoleCookieMiddleware,
} from '~/middleware/active-role-cookie';
import { PermissionsProvider } from '~/contexts/permissions-context';
import { SseNotificationsProvider } from '~/contexts/sse-notifications-context';

export const middleware = [
  affineCookieMiddleware,
  activeRoleCookieMiddleware,
  contextInitMiddleware,
];

/**
 * Prevents unnecessary user data fetching on every page navigation.
 *
 * We only need to refetch when:
 * 1. User authentication state changes (login, signup, logout, password reset)
 * 2. After form submissions that might update user data (actionStatus != null)
 * 3. When completing onboarding (which updates user profile)
 */
export function shouldRevalidate(args: ShouldRevalidateFunctionArgs) {
  // Don't revalidate if the action was from autosave
  if (args.formAction?.includes('/autosave')) {
    return false;
  }

  // Revalidate after profile updates regardless of actionStatus
  if (args.formAction === $path('/onboarding')) {
    return true;
  }

  // Revalidate after a role switch: the action redirects, and redirects reach
  // shouldRevalidate with actionStatus undefined, so the actionStatus check
  // below never fires — without this the navbar keeps showing the old role.
  if (args.formAction === $path('/active-role')) {
    return true;
  }

  // Always revalidate after mutations (form submissions)
  if (args.actionStatus != null) {
    return true;
  }

  // Revalidate when navigating to/from auth-related routes where user state changes
  const currentPath = new URL(args.currentUrl).pathname;
  const nextPath = new URL(args.nextUrl).pathname;

  const authPaths = [
    $path('/login'),
    $path('/signup'),
    $path('/logout'),
    $path('/onboarding'),
    $path('/signup/verify'),
    $path('/forgot-password'),
    $path('/reset-password'),
  ];
  const isLeavingAuth = authPaths.some(path => currentPath.startsWith(path));
  const isEnteringAuth = authPaths.some(path => nextPath.startsWith(path));

  if (isLeavingAuth || isEnteringAuth) {
    return true;
  }

  return false;
}

export async function loader(args: Route.LoaderArgs) {
  const url = new URL(args.request.url);
  const isLogin = url.searchParams.get('auth') === 'login';
  const isSignup = url.searchParams.get('auth') === 'signup';

  // Skip API call for unauthenticated users - no token means no user data to fetch
  const accessToken = args.context.get(accessTokenContext);
  const userResponse = accessToken
    ? await getUser(args.context)
    : { data: null, error: null, response: new Response(null) };

  const rudderstackWriteKey = env.RUDDERSTACK_WRITE_KEY;
  const envName = env.ENVIRONMENT;
  const permissions = args.context.get(permissionsContext) ?? [];

  // Re-validate the session's active role against the roles minted into the
  // access token; a role the user no longer holds falls back to accountType.
  const availableRoles = accessToken ? getRolesFromToken(accessToken) : [];
  const activeRoleFromSession = getActiveRole(args.context);
  const activeRole =
    activeRoleFromSession && availableRoles.includes(activeRoleFromSession)
      ? activeRoleFromSession
      : userResponse.data?.accountType;

  // Clear a stale activeRole from the session so route guards stop steering
  // the user into a shell for a role they no longer hold. Skipped while a
  // token refresh is committing this request's session (cookie token differs
  // from the context token) — committing the pre-refresh session here would
  // clobber the rotated tokens; the cleanup then happens on the next request.
  const session = await getSession(args.request);
  const storedActiveRole = session.get('activeRole');
  if (
    accessToken &&
    storedActiveRole &&
    (storedActiveRole.userId !== session.get('userId') ||
      !availableRoles.includes(storedActiveRole.role)) &&
    session.get('accessToken') === accessToken
  ) {
    session.unset('activeRole');
    const cookieRef = args.context.get(activeRoleCookieContext);
    if (cookieRef) {
      cookieRef.value = await sessionStorage.commitSession(session);
    }
  }

  return {
    isLogin,
    isSignup,
    isAuthView: isLogin || isSignup,
    user: userResponse.data,
    rudderstackWriteKey,
    envName,
    permissions,
    activeRole,
    availableRoles,
  };
}

export default function Layout() {
  const loaderData = useLoaderData<typeof loader>();
  const error = useRouteError();
  const isAuthView = loaderData.isAuthView;
  const user = loaderData.user;
  const hasInitialized = useRef(false);
  const location = useLocation();
  const userIdentifiedRef = useRef(false);

  const navigation = useNavigation();

  // Check if current navigation is an autosave (not a regular navigation)
  const isAutosave = navigation.formData?.get(AUTOSAVE_INTENT.key) === AUTOSAVE_INTENT.value;

  useEffect(() => {
    if (error || hasInitialized.current) {
      return;
    }

    analyticsInit({
      rudderstackWriteKey: loaderData.rudderstackWriteKey,
      envName: loaderData.envName,
    });

    hasInitialized.current = true;
  }, [error, loaderData.envName, loaderData.rudderstackWriteKey]);

  useEffect(() => {
    rudderAnalytics.page();
  }, [location]);

  useEffect(() => {
    if (!user || userIdentifiedRef.current === true) {
      return;
    }
    rudderAnalytics.identify(user.id, { UserType: user.accountType });
    userIdentifiedRef.current = true;
  }, [user]);

  return (
    <>
      <div className={cn(navigation.state !== 'idle' && !isAutosave && 'opacity-50')}>
        <PermissionsProvider permissions={loaderData.permissions}>
          <SseNotificationsProvider activeRole={loaderData.activeRole ?? user?.accountType}>
            <Outlet />
          </SseNotificationsProvider>
        </PermissionsProvider>
        {isAuthView && (
          <AuthContainer>
            {loaderData.isLogin && <Login />}
            {loaderData.isSignup && <SignUp />}
          </AuthContainer>
        )}
      </div>
    </>
  );
}
